/**
 * Downgrade Confirmation Modal
 * 
 * Modal dialog that displays downgrade confirmation details
 * Shows warning banner, what changes, and what user will keep
 * 
 * @package @ideoz/ui
 */

import React from 'react';
import { X, AlertTriangle, Check, ArrowRight, Circle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DialogDescription,
  DialogClose,
} from '@ideoz/ui';
import Icon from './Icon';

export interface DowngradeConfirmationModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlanName: string;
  currentPlanPrice: number;
  currentPlanCredits: number;
  nextBillingDate: string;
}

export default function DowngradeConfirmationModal({
  isOpen,
  isLoading = false,
  onClose,
  onConfirm,
  currentPlanName,
  currentPlanPrice,
  currentPlanCredits,
  nextBillingDate,
}: DowngradeConfirmationModalProps): JSX.Element {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex items-center justify-between">
          <DialogTitle className="font-semibold text-[#1D2939]">
            Downgrade to free
          </DialogTitle>
          <DialogClose
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#717680] transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </DialogHeader>

        <DialogDescription className="text-xs text-[#717680]">
          You're about to downgrade from {currentPlanName} to Free.
        </DialogDescription>

        {/* Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <Icon icon={AlertTriangle} size={20} stroke="#DC2626" />
          <div className="flex-1">
            <p className="text-sm text-red-800">
              Your downgrade will take effect at the end of your current billing period ({nextBillingDate}). You'll continue to have access to {currentPlanName} features until then.
            </p>
          </div>
        </div>

        {/* What changed section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#252B37]">What changed</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Icon icon={ArrowRight} size={16} stroke="#9CA3AF" />
              <span className="text-sm text-[#717680]">
                Monthly credits: 250 → 40
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon icon={ArrowRight} size={16} stroke="#9CA3AF" />
              <span className="text-sm text-[#717680]">
                Access limited to basic exports
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon icon={ArrowRight} size={16} stroke="#9CA3AF" />
              <span className="text-sm text-[#717680]">
                X projects will not be able to access
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon icon={Circle} size={16} stroke="#9CA3AF" strokeWidth={1.5} fill="none" />
              <span className="text-sm text-[#717680]">
                Monthly charge: ${currentPlanPrice} → $0
              </span>
            </div>
          </div>
        </div>

        {/* You will keep section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#252B37]">You will keep</h3>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Icon icon={Check} size={18} stroke="#17B26A" />
              <span className="text-sm text-[#717680]">
                All your existing projects and data
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon icon={Check} size={18} stroke="#17B26A" />
              <span className="text-sm text-[#717680]">
                Access to {currentPlanName} plan features until {nextBillingDate}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon icon={Check} size={18} stroke="#17B26A" />
              <span className="text-sm text-[#717680]">
                Ability to upgrade anytime
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex">
          <Button
            variant="secondary-gray"
            onClick={onClose}
            disabled={isLoading}
            
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
           variant="destructive"
          >
            {isLoading ? 'Processing...' : 'Downgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
